import { createContext, useCallback, useContext } from 'react';
import { ColorTheme } from '@figma/fpl-themes';
import { createPortal } from 'react-dom';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  type DialogActionsProps,
  type DialogContentProps,
  type DialogProps,
  type DialogTitleProps,
  type SxProps,
} from '@mui/material';
import { Modal, type ModalWidth, type ModalHeight, type ModalRootProps } from '@figma/fpl-components';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { TextLgSb } from '@/UI/AppTypography/AppTypography';

export type { DialogActionsProps, DialogContentProps, DialogProps, DialogTitleProps };

/**
 * Unified width override, applied the same way on both paths: a preset (mapped to FPL's own
 * sm/md/lg on the FPL path, and to a fixed px value on the legacy MUI path) or an explicit
 * pixel number (forwarded as-is on both paths). Takes priority over `maxWidth` /
 * `slotProps.paper.sx.width`, which remain as fallbacks for callers that haven't migrated yet.
 */
export type AppDialogWidth = 'sm' | 'md' | 'lg' | number;

export interface AppDialogProps extends DialogProps {
  width?: AppDialogWidth;
  /**
   * Stacking order, applied on both paths (MUI merges it into `sx`; the FPL path — which otherwise drops
   * `sx` — wraps the portaled modal in a positioned z-index context; not lifted with `disablePortal`). An
   * explicit prop rather than `sx.zIndex` so callers already passing `sx.zIndex` are unaffected on the FPL
   * path. Use for critical-alert dialogs that must sit above ordinary dialogs.
   */
  zIndex?: number;
}

const MUI_WIDTH_PX: Record<'sm' | 'md' | 'lg', number> = {
  sm: 320,
  md: 440,
  lg: 560,
};

function resolveMuiWidthPx(width: AppDialogWidth): number {
  return typeof width === 'number' ? width : MUI_WIDTH_PX[width];
}

function withMuiWidthSlotProps(slotProps: DialogProps['slotProps'], widthPx: number): DialogProps['slotProps'] {
  const paper = slotProps?.paper as Record<string, unknown> | undefined;
  const paperSx = paper?.sx as Record<string, unknown> | undefined;
  return {
    ...slotProps,
    paper: {
      ...paper,
      sx: { ...paperSx, width: widthPx },
    },
  };
}

// ── FPL mode context ──────────────────────────────────────────────────────────
// Propagates the flag-on state to sub-components so the feature flag is read
// only once at the AppDialog root and sub-components branch on context.
const FplModalContext = createContext(false);

// ── MUI legacy path ───────────────────────────────────────────────────────────
const dialogSx = {
  '& .MuiDialog-paper': {
    backgroundImage: 'none',
    borderRadius: '8px',
    border: `1px solid ${color.White08_T}`,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function extractWidthFromSlotProps(slotProps: DialogProps['slotProps']): ModalWidth | undefined {
  const paperSx = (slotProps?.paper as Record<string, unknown> | undefined)?.sx as Record<string, unknown> | undefined;
  return typeof paperSx?.width === 'number' ? paperSx.width : undefined;
}

function dialogContainerSx(sx: DialogProps['sx']): Record<string, unknown> | undefined {
  const container = (sx as Record<string, unknown> | undefined)?.['& .MuiDialog-container'];
  if (container == null || typeof container !== 'object' || Array.isArray(container)) {
    return undefined;
  }
  return container as Record<string, unknown>;
}

// MUI dialogs that top-align their container (`alignItems: 'flex-start'`) do so because the
// content is tall / variable-height. The FPL equivalent is `dynamic` (pinned near the top and
// grows downwards); everything else stays `fixed` (vertically centered).
function extractHeightFromProps(sx: DialogProps['sx'], fullScreen: DialogProps['fullScreen']): ModalHeight {
  if (fullScreen) {
    return 'fullscreen';
  }
  return dialogContainerSx(sx)?.alignItems === 'flex-start' ? 'dynamic' : 'fixed';
}

/**
 * Fixed top offset from the MUI container (`paddingTop`). FPL's root padding is `10dvh 0`, which
 * is what pins a `dynamic` modal; without this the caller's offset is dropped with the rest of `sx`.
 */
function extractContainerPaddingTop(sx: DialogProps['sx']): string | number | undefined {
  const paddingTop = dialogContainerSx(sx)?.paddingTop;
  return typeof paddingTop === 'string' || typeof paddingTop === 'number' ? paddingTop : undefined;
}

// FPL's close source enum, derived from the public Modal API (the underlying
// `DialogCloseSource` type isn't exported on its own).
type DialogCloseSource = NonNullable<Parameters<NonNullable<ModalRootProps['onClose']>>[0]>['source'];
type MuiCloseReason = NonNullable<Parameters<NonNullable<DialogProps['onClose']>>[1]>;

// Map the FPL close sources that have a MUI equivalent; anything else (e.g.
// `'button'`, `'other'`) is forwarded to the MUI handler as-is.
const FPL_SOURCE_TO_MUI_REASON: Partial<Record<DialogCloseSource, MuiCloseReason>> = {
  escape: 'escapeKeyDown',
  outside: 'backdropClick',
};

// ── FPL path ──────────────────────────────────────────────────────────────────
function FplAppDialog({
  open,
  onClose: muiOnClose,
  'aria-label': ariaLabel,
  children,
  slotProps,
  hideBackdrop,
  sx,
  disablePortal,
  fullScreen,
  width: widthProp,
  zIndex,
}: AppDialogProps) {
  const width: ModalWidth = widthProp ?? extractWidthFromSlotProps(slotProps) ?? 'fit-content';
  const height: ModalHeight = extractHeightFromProps(sx, fullScreen);
  const containerPaddingTop = extractContainerPaddingTop(sx);
  // Portalling to <body> escapes the app's ColorTheme, so the mode is re-applied below.
  const themeMode = useThemeMode();

  // When no onClose handler is provided the dialog cannot be dismissed, which
  // matches MUI's implicit behaviour (e.g. TermsOfServiceDialog).
  const preventUserClose = muiOnClose === undefined;

  // Bridge the FPL close callback (opts.source) to the MUI-shaped callback
  // (event, reason) that callers provide.
  const handleClose = useCallback(
    (opts: { source: DialogCloseSource }) => {
      if (preventUserClose) return;
      const reason = FPL_SOURCE_TO_MUI_REASON[opts.source] ?? (opts.source as MuiCloseReason);
      muiOnClose({}, reason);
    },
    [preventUserClose, muiOnClose],
  );

  let modal: React.ReactNode = (
    <Modal.Root
      open={open ?? false}
      onClose={handleClose}
      width={width}
      height={height}
      preventUserClose={preventUserClose}
    >
      {/* FPL styled components take no aria-* attributes, so a dialog without a visible
          Modal.Title is named through Modal.HiddenTitle instead. */}
      {ariaLabel !== undefined ? <Modal.HiddenTitle>{ariaLabel}</Modal.HiddenTitle> : null}
      {children}
    </Modal.Root>
  );

  // FPL's Modal.Root exposes no equivalent of MUI's `hideBackdrop`, so when it's
  // requested we render the modal inside a display:contents wrapper (no layout box)
  // and make the backdrop element transparent. The backdrop is an in-tree
  // `[data-backdrop]` descendant, so the selector reaches it.
  // The same wrapper overrides the root's `10dvh` padding when the MUI container sets `paddingTop`
  // (the dialog element is the padding box). `:not([data-modal-fullscreen])` leaves the small-viewport
  // fullscreen padding at 0.
  const rootOverrides: Record<string, unknown> = {};
  if (hideBackdrop) {
    rootOverrides['& [data-backdrop]'] = { background: 'transparent' };
  }
  if (containerPaddingTop != null) {
    rootOverrides['&& [role="dialog"]:not([data-modal-fullscreen])'] = { paddingTop: containerPaddingTop };
  }
  if (Object.keys(rootOverrides).length > 0) {
    modal = <Box sx={{ display: 'contents', ...rootOverrides }}>{modal}</Box>;
  }

  // By default we portal to <body>, matching MUI's Dialog. FPL otherwise renders the modal
  // inline and positions it `fixed`, so an ancestor with transform/filter/contain would trap it
  // inside the container; portaling escapes that. `disablePortal` opts back into inline rendering.
  if (disablePortal) {
    return modal;
  }

  // Wrap the portaled modal in a positioned z-index context (when requested) so a critical-alert dialog stacks
  // above ordinary dialogs — FPL's Modal.Root is otherwise fixed at z-index 10. Portal-only: the wrapper is a
  // block-level box, layout-neutral only because it is portaled to <body>; zIndex is not lifted with
  // `disablePortal` (no caller pairs them).
  return createPortal(
    <ColorTheme brand="weave" mode={themeMode}>
      {zIndex !== undefined ? <div style={{ position: 'relative', zIndex }}>{modal}</div> : modal}
    </ColorTheme>,
    document.body,
  );
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Temporary migration adapter (ADR-002).
 *
 * Flag OFF (default): renders the legacy MUI Dialog unchanged.
 * Flag ON  (FF_FPL_MIGRATION): renders FPL Modal.Root + Modal.Contents.
 *
 * Key gap: MUI-only props (sx, slotProps.paper, transition slots,
 * disableEnforceFocus) are silently ignored on the FPL path per ADR-001. Exception: the `zIndex` prop is
 * honored on both paths (MUI merges it into `sx`; the FPL path wraps the portaled modal in a positioned
 * z-index context) — a `zIndex` set via `sx` is NOT read, so existing `sx.zIndex` callers keep their FPL
 * behavior.
 *
 * Width is resolved in priority order:
 * 1. `width` ({@link AppDialogWidth}) — a preset ('sm'/'md'/'lg') or explicit pixel number.
 * 2. An explicit `slotProps.paper.sx.width` (number).
 * 3. `'fit-content'` (FPL path) / MUI's own default sizing.
 *
 * Exception: sx['& .MuiDialog-container'].alignItems === 'flex-start' (a top-aligned MUI
 * dialog) maps to FPL height "dynamic" (top-pinned, grows down); otherwise height is "fixed"
 * (vertically centered).
 */
export function AppDialog({ sx, disablePortal, width, slotProps, zIndex, ...props }: AppDialogProps) {
  const isFpl = useFplMigrationFlag();

  if (isFpl) {
    return (
      <FplModalContext.Provider value={true}>
        <FplAppDialog
          sx={sx}
          disablePortal={disablePortal}
          width={width}
          slotProps={slotProps}
          zIndex={zIndex}
          {...props}
        />
      </FplModalContext.Provider>
    );
  }

  return (
    <FplModalContext.Provider value={false}>
      <Dialog
        sx={zIndex !== undefined ? { ...dialogSx, ...sx, zIndex } : { ...dialogSx, ...sx }}
        disablePortal={disablePortal}
        slotProps={width !== undefined ? withMuiWidthSlotProps(slotProps, resolveMuiWidthPx(width)) : slotProps}
        {...props}
      />
    </FplModalContext.Provider>
  );
}

/**
 * The dialog's header region (the bar that holds the title, and optionally a
 * close button or secondary action).
 *
 * Flag OFF: legacy MUI DialogTitle.
 * Flag ON:  renders Modal.Header (or Modal.CustomHeader for the space-between
 *           layout).
 */
export function AppDialogHeader({ children, sx, ...rest }: DialogTitleProps) {
  const isFpl = useContext(FplModalContext);

  if (isFpl) {
    const isSpaceBetween = (sx as Record<string, unknown> | undefined)?.justifyContent === 'space-between';
    if (isSpaceBetween) {
      return (
        <Modal.CustomHeader
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            paddingRight: '2.5rem',
            paddingLeft: '0.5rem',
            minHeight: '2.5rem',
          }}
        >
          {children}
        </Modal.CustomHeader>
      );
    }
    return <Modal.Header>{children}</Modal.Header>;
  }

  return (
    <DialogTitle sx={sx} {...rest}>
      {children}
    </DialogTitle>
  );
}

/**
 * The dialog's title text. Render it inside an {@link AppDialogHeader}.
 */
export function AppDialogTitle({ children }: { children: React.ReactNode }) {
  const isFpl = useContext(FplModalContext);

  if (isFpl) {
    return <Modal.Title>{children}</Modal.Title>;
  }

  return <TextLgSb>{children}</TextLgSb>;
}

/**
 * Flag OFF: legacy MUI DialogContent.
 * Flag ON:  renders Modal.Body.
 *
 * Gap: most sx props are dropped on the FPL path. Exception: sx.p is
 * forwarded as Modal.Body's padding prop — numbers are multiplied by 8 to
 * convert from MUI spacing units to px.
 *
 * Default: without an explicit sx.p, the body gets a symmetric 16px padding on all sides —
 * overriding Modal.Body's own default (8px vertical / 16px horizontal).
 */
export function AppDialogContent({ children, sx, ...rest }: DialogContentProps) {
  const isFpl = useContext(FplModalContext);

  if (isFpl) {
    const rawP = (sx as Record<string, unknown> | undefined)?.p;
    const padding = typeof rawP === 'number' ? rawP * 8 : typeof rawP === 'string' ? rawP : spacer.spacer3;
    return <Modal.Body padding={padding}>{children}</Modal.Body>;
  }

  return (
    <DialogContent sx={sx} {...rest}>
      {children}
    </DialogContent>
  );
}

/**
 * Flag OFF: legacy MUI DialogActions.
 * Flag ON:  renders Modal.Footer + Modal.ActionStrip by default.
 *
 * Exception: when sx requests `justifyContent: 'space-between'` (a footer with
 * content pushed to both edges, e.g. an info label on the left and actions on
 * the right) the default ActionStrip can't express that layout, so we render
 * Modal.CustomFooter with a space-between flex container instead.
 *
 * Gap: other sx / style / className values are dropped on the FPL path.
 * Children are still MUI-based buttons at this migration stage; button
 * migration is tracked separately.
 */
export function AppDialogActions({ children, sx, ...rest }: DialogActionsProps) {
  const isFpl = useContext(FplModalContext);

  if (isFpl) {
    const isSpaceBetween = (sx as Record<string, unknown> | undefined)?.justifyContent === 'space-between';
    if (isSpaceBetween) {
      return (
        <Modal.CustomFooter
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacer.spacer2,
            paddingTop: spacer.spacer2,
            paddingRight: spacer.spacer2,
            paddingBottom: spacer.spacer2,
            paddingLeft: spacer.spacer3,
          }}
        >
          {children}
        </Modal.CustomFooter>
      );
    }
    return (
      <Modal.Footer>
        <Modal.ActionStrip>{children}</Modal.ActionStrip>
      </Modal.Footer>
    );
  }

  return (
    <DialogActions sx={sx} {...rest}>
      {children}
    </DialogActions>
  );
}

/**
 * A close button for AppDialog that automatically hides itself when the FPL path is
 * active (FF_FPL_MIGRATION ON), because FPL Modal renders its own built-in close affordance.
 *
 * Drop-in replacement for bare <AppXBtn> inside any AppDialog descendant.
 */
export function AppDialogXBtn(props: React.ComponentProps<typeof AppXBtn>) {
  const isFpl = useContext(FplModalContext);
  if (isFpl) return null;
  return <AppXBtn {...props} />;
}

/**
 * A section boundary inside an AppDialog.
 *
 * Flag OFF: transparent passthrough — renders children directly with no wrapper
 * so MUI Dialog composition is unchanged. If `muiSx` is provided the children
 * are wrapped in a Box with those styles, which lets callers apply a visual card
 * treatment (background, border, etc.) only on the legacy MUI path.
 * Flag ON:  renders Modal.Contents, allowing multiple independent content panes
 * inside a single Modal.Root (e.g. share panel + community panel). `muiSx` is
 * ignored on the FPL path.
 *
 * Every AppDialog must have at least one AppDialogSection wrapping its content.
 * Modals with a single section use one AppDialogSection; modals with multiple
 * independent panes (e.g. ShareWorkflowModal) use one per pane.
 */
export function AppDialogSection({
  children,
  sx,
  withBody,
}: {
  children: React.ReactNode;
  sx?: SxProps;
  withBody?: boolean;
}) {
  const isFpl = useContext(FplModalContext);
  if (isFpl) {
    return (
      <Modal.Contents>{withBody ? <AppDialogContent sx={sx}>{children}</AppDialogContent> : children}</Modal.Contents>
    );
  }
  if (sx) return <Box sx={sx}>{children}</Box>;
  return children;
}
